import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import type { ReceivingLine, UpdateQCStatusInput } from '../../types';

type QCStatusInput = 'passed' | 'failed';

interface UpdateQCModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: UpdateQCStatusInput) => Promise<void>;
  line: ReceivingLine | null;
  isLoading?: boolean;
}

export function UpdateQCModal({
  open,
  onClose,
  onSubmit,
  line,
  isLoading,
}: UpdateQCModalProps) {
  const [qcStatus, setQcStatus] = useState<QCStatusInput | ''>('');
  const [qcNotes, setQcNotes] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qcStatus) return;

    try {
      await onSubmit({
        qc_status: qcStatus,
        qc_notes: qcNotes || undefined,
      });
      toast.success('QC status updated successfully');
      handleClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to update QC status: ${message}`);
      console.error('Error updating QC status:', error);
      // Keep form open for retry
    }
  };

  const handleClose = () => {
    setQcStatus('');
    setQcNotes('');
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Update QC Status</DialogTitle>
        </DialogHeader>
        {line && (
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {/* Line info */}
              <div className="bg-muted p-3 rounded-md space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">SKU:</span>
                  <span className="font-mono">{line.sku || line.sku_code || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Received Qty:</span>
                  <span className="font-medium">{line.received_quantity}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Current QC:</span>
                  <span>{line.qc_status || 'Not set'}</span>
                </div>
              </div>

              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="qc_status">QC Status *</Label>
                  <Select value={qcStatus} onValueChange={(v) => setQcStatus(v as QCStatusInput)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select QC status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="passed">Passed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="qc_notes">QC Notes</Label>
                  <Textarea
                    id="qc_notes"
                    value={qcNotes}
                    onChange={(e) => setQcNotes(e.target.value)}
                    placeholder="Notes about QC inspection"
                    rows={3}
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading || !qcStatus}>
                {isLoading ? 'Updating...' : 'Update QC'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
